import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ColourAlertsForOrderTime, ColourAlertTimeRange } from "resbutler-utils/types";

export interface SettingReducerTypes {
  isFixed: boolean;
  isCollapsed: boolean;
  isBoxed: boolean;
  isFloat: boolean;
  asideHover: boolean;
  asideScrollbar: boolean;
  isCollapsedText: boolean;
  offsidebarOpen: boolean;
  asideToggled: boolean;
  showUserBlock: boolean;
  horizontal: boolean;
  useFullLayout: boolean;
  hiddenFooter: boolean;
  showCustomerName: boolean;
  splitItemsByTable: boolean;
  showHiddenDockets: boolean;
  showTableServiceDockets: boolean;
  showCounterServiceDockets: boolean;
  showPickupDockets: boolean;
  showVacatedBookingDockets: boolean;
  singleDocketPerTable: boolean;
  useGroupedDocketLayout: boolean;
  colourAlertsForOrderTime: ColourAlertsForOrderTime;
}

export const initialSettingsState: SettingReducerTypes = {
  /* Layout fixed. Scroll content only */
  isFixed: true,
  /* Sidebar collapsed */
  isCollapsed: false,
  /* Boxed layout */
  isBoxed: false,
  /* Floating sidebar */
  isFloat: false,
  /* Sidebar show menu on hover only */
  asideHover: false,
  /* Show sidebar scrollbar (dont' hide it) */
  asideScrollbar: false,
  /* Sidebar collapsed with big icons and text */
  isCollapsedText: false,
  /* Toggle for the offsidebar */
  offsidebarOpen: false,
  /* Toggle for the sidebar offcanvas (mobile) */
  asideToggled: false,
  /* Toggle for the sidebar user block */
  showUserBlock: false,
  /* Enables layout horizontal */
  horizontal: false,
  /* Full size layout */
  useFullLayout: false,
  /* Hide footer */
  hiddenFooter: false,
  /* Show Customer Name */
  showCustomerName: true,
  splitItemsByTable: false,
  showHiddenDockets: false,
  showTableServiceDockets: true,
  showCounterServiceDockets: true,
  showPickupDockets: true,
  showVacatedBookingDockets: true,
  singleDocketPerTable: false,
  useGroupedDocketLayout: undefined,
  colourAlertsForOrderTime: {
    enabled: true,
    alerts: {
      green: {
        _key: "green",
        colour: "#27c24c",
        order: 1,
        timeRange: {
          min: 0,
          max: 15,
        },
      },
      yellow: {
        _key: "yellow",
        colour: "#f6c500",
        order: 2,
        timeRange: {
          min: 16,
          max: 30,
        },
      },
      red: {
        _key: "red",
        colour: "#ee4242",
        order: 3,
        timeRange: {
          min: 31,
        },
      },
    },
  },
};

const settingsSlice = createSlice({
  name: "settings",
  initialState: initialSettingsState,
  reducers: {
    toggleSetting(state, action: PayloadAction<keyof SettingReducerTypes>) {
      const key = action.payload;
      (state[key] as boolean) = !state[key];
    },
    changeSetting(state, action: PayloadAction<{ name: keyof SettingReducerTypes; value: boolean }>) {
      const { name, value } = action.payload;
      (state[name] as boolean) = value;
    },
    toggleColourAlerts(state) {
      state.colourAlertsForOrderTime.enabled = !state.colourAlertsForOrderTime.enabled;
    },
    updateColourAlertRange(state, action: PayloadAction<{ _key: number; field: keyof ColourAlertTimeRange; value: number }>) {
      const { _key, field, value } = action.payload;
      const alert = state.colourAlertsForOrderTime.alerts[_key];
      if (alert) {
        alert.timeRange[field] = value;
      }
    },
  },
});

export const { toggleSetting, changeSetting, toggleColourAlerts, updateColourAlertRange } = settingsSlice.actions;
export default settingsSlice.reducer;
